// src/pages/Companies.jsx
import { useState, useEffect } from 'react';
import { useGraphQL } from '../graphql';
import DataTable from '../components/DataTable';
import Modal, { ModalForm } from '../components/Modal';
import { StatusBadge } from '../components/Badge';
import { PlusIcon, EyeIcon, PencilIcon, BuildingOfficeIcon, SparklesIcon, UserGroupIcon, UserPlusIcon, TrashIcon, NoSymbolIcon } from '@heroicons/react/24/outline';
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid';
import FeatureManagementModal from '../components/FeatureManagementModal';
import TeamManagementModal from '../components/TeamManagementModal';
import { IdField } from '../components/CopyButton';

const Companies = () => {
    const { executeQuery, executeMutation, queries, mutations, loading, error } = useGraphQL();
    const [companies, setCompanies] = useState([]);
    const [selectedCompany, setSelectedCompany] = useState(null);
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showFeatureModal, setShowFeatureModal] = useState(false);
    const [showTeamModal, setShowTeamModal] = useState(false);
    const [showSetRepModal, setShowSetRepModal] = useState(false);
    const [setRepCompany, setSetRepCompany] = useState(null);
    const [setRepLoading, setSetRepLoading] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleteCompanyTarget, setDeleteCompanyTarget] = useState(null);
    const [deleteConfirmText, setDeleteConfirmText] = useState('');
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [filters, setFilters] = useState({ active: '' });
    const [formData, setFormData] = useState({
        name: '',
        size: 'SMALL',
        industry: '',
        location: {
            address: '',
            city: '',
            state: '',
            postalCode: '',
            country: '',
            nutsCode: '',
            latitude: null,
            longitude: null
        },
        website: '',
        description: '',
        active: true
    });
    const [submitLoading, setSubmitLoading] = useState(false);

    useEffect(() => {
        loadCompanies();
    }, [filters]);

    const loadCompanies = async () => {
        try {
            const filterVars = {
                active: filters.active !== '' ? filters.active === 'true' : undefined,
                limit: 100,
                offset: 0
            };
            const data = await executeQuery(queries.getCompanies(), filterVars);
            setCompanies(data.adminCompanies || []);
        } catch (err) {
            console.error('Failed to load companies:', err);
        }
    };

    const loadCompanyDetail = async (companyId) => {
        try {
            const data = await executeQuery(queries.getCompany(), { id: companyId });
            setSelectedCompany(data.adminCompany);
        } catch (err) {
            console.error('Failed to load company details:', err);
        }
    };

    const handleCreateCompany = async (e) => {
        e.preventDefault();
        setSubmitLoading(true);
        try {
            const result = await executeMutation(mutations.createCompany, {
                input: {
                    name: formData.name,
                    size: formData.size,
                    industry: formData.industry || undefined,
                    location: formData.location,
                    website: formData.website || undefined,
                    description: formData.description || undefined
                }
            });

            if (result.createCompany.success) {
                setShowCreateModal(false);
                resetForm();
                await loadCompanies();
            } else {
                alert('Error: ' + result.createCompany.error);
            }
        } catch (err) {
            console.error('Failed to create company:', err);
            alert('Failed to create company: ' + err.message);
        } finally {
            setSubmitLoading(false);
        }
    };

    const handleUpdateCompany = async (e) => {
        e.preventDefault();
        setSubmitLoading(true);
        try {
            const result = await executeMutation(mutations.adminUpdateCompany, {
                id: selectedCompany.id,
                input: {
                    name: formData.name,
                    size: formData.size,
                    industry: formData.industry || undefined,
                    location: formData.location,
                    website: formData.website || undefined,
                    description: formData.description || undefined,
                    active: formData.active
                }
            });

            if (result.adminUpdateCompany.success) {
                setShowEditModal(false);
                setSelectedCompany(null);
                resetForm();
                await loadCompanies();
            } else {
                alert('Error: ' + result.adminUpdateCompany.error);
            }
        } catch (err) {
            console.error('Failed to update company:', err);
            alert('Failed to update company: ' + err.message);
        } finally {
            setSubmitLoading(false);
        }
    };

    const resetForm = () => {
        setFormData({
            name: '',
            size: 'SMALL',
            industry: '',
            location: {
                address: '',
                city: '',
                state: '',
                postalCode: '',
                country: '',
                nutsCode: '',
                latitude: null,
                longitude: null,
            },
            website: '',
            description: '',
            active: true
        });
    };

    const openDetailModal = async (company) => {
        await loadCompanyDetail(company.id);
        setShowDetailModal(true);
    };

    const openEditModal = (company) => {
        setSelectedCompany(company);
        setFormData({
            name: company.name,
            size: company.size,
            industry: company.industry || '',
            location: company.location || {
                address: '',
                city: '',
                state: '',
                postalCode: '',
                country: '',
                nutsCode: '',
                latitude: null,
                longitude: null
            },
            website: company.website || '',
            description: company.description || '',
            active: company.active
        });
        setShowEditModal(true);
    };

    const openFeatureModal = (company) => {
        setSelectedCompany(company);
        setShowFeatureModal(true);
    };

    const openTeamModal = (company) => {
        setSelectedCompany(company);
        setShowTeamModal(true);
    };

    // Helper to find company representative
    const getRepresentative = (company) => {
        return company.users?.find(u => u.isCompanyRepresentative);
    };

    const openDeleteModal = (company) => {
        setDeleteCompanyTarget(company);
        setDeleteConfirmText('');
        setShowDeleteModal(true);
    };

    const closeDeleteModal = () => {
        setShowDeleteModal(false);
        setDeleteCompanyTarget(null);
        setDeleteConfirmText('');
    };

    const handleDeleteCompany = async () => {
        if (!deleteCompanyTarget) return;
        if (deleteConfirmText !== deleteCompanyTarget.name) return;
        setDeleteLoading(true);
        try {
            const result = await executeMutation(mutations.deleteCompany, { id: deleteCompanyTarget.id });
            if (result.deleteCompany.success) {
                closeDeleteModal();
                await loadCompanies();
            } else {
                alert('Error: ' + (result.deleteCompany.error || result.deleteCompany.message || 'Failed to delete company'));
            }
        } catch (err) {
            console.error('Failed to delete company:', err);
            alert('Failed to delete company: ' + err.message);
        } finally {
            setDeleteLoading(false);
        }
    };

    const handleSetRepresentative = async (userId, companyId) => {
        setSetRepLoading(true);
        try {
            const result = await executeMutation(mutations.setCompanyRepresentative, {
                userId,
                companyId
            });
            if (result.setCompanyRepresentative?.success) {
                setShowSetRepModal(false);
                setSetRepCompany(null);
                await loadCompanies();
            } else {
                alert('Error: ' + (result.setCompanyRepresentative?.error || 'Failed to set representative'));
            }
        } catch (err) {
            alert('Failed to set representative: ' + err.message);
        } finally {
            setSetRepLoading(false);
        }
    };

    // Bulk-cancel every PENDING job of one company — support tool for runaway
    // per-company fan-outs (e.g. a misconfigured scheduled workflow). Running
    // jobs are not touched.
    const handleCancelPendingJobs = async (company) => {
        if (!window.confirm(`Cancel ALL pending jobs for "${company.name}"? Running jobs are not affected.`)) return;
        try {
            const result = await executeMutation(mutations.adminCancelPendingJobsByCompany, { companyId: company.id });
            const r = result.adminCancelPendingJobsByCompany;
            if (r?.success) {
                alert(`Cancelled ${r.deletedCount} pending job(s) for ${company.name}.`);
            } else {
                alert('Error: ' + (r?.error || r?.message || 'Failed to cancel pending jobs'));
            }
        } catch (err) {
            alert('Failed to cancel pending jobs: ' + err.message);
        }
    };

    // Tier user limits
    const getTierUserLimit = (tier) => {
        switch (tier) {
            case 'BASIC': return 1;
            case 'PRO': return 5;
            case 'ENTERPRISE': return 999;
            default: return 1;
        }
    };

    const columns = [
        {
            key: 'name',
            title: 'Company',
            sortable: true,
            render: (name, company) => (
                <div className="flex items-center">
                    <div className="bg-primary p-2 mr-3">
                        <BuildingOfficeIcon className="h-4 w-4 text-white" />
                    </div>
                    <div>
                        <div className="font-medium text-white">{name}</div>
                        <div className="text-sm text-gray-400">{company.industry}</div>
                    </div>
                </div>
            )
        },
        {
            key: 'size',
            title: 'Size',
            sortable: true,
            render: (size) => (
                <span className="px-2 py-1 bg-gray-700 text-gray-300 text-sm">
                    {size?.replace('_', ' ')}
                </span>
            )
        },
        {
            key: 'location',
            title: 'Location',
            render: (location) => {
                if (!location) return <span className="text-gray-400">-</span>;
                const parts = [];
                if (location.address) parts.push(location.address);
                if (location.city) parts.push(location.city);
                if (location.state) parts.push(location.state);
                if (location.postalCode) parts.push(location.postalCode);
                if (location.country) parts.push(location.country);
                if (location.nutsCode) parts.push(location.nutsCode);
                if (location.latitude) parts.push(`Lat: ${location.latitude}`);
                if (location.longitude) parts.push(`Lng: ${location.longitude}`);
                return <span className="text-white text-sm">{parts.join(', ')}</span>;
            }
        },
        {
            key: 'representative',
            title: 'Representative',
            render: (_, company) => {
                const rep = getRepresentative(company);
                if (!rep) {
                    if (company.users?.length > 0) {
                        return (
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setSetRepCompany(company);
                                    setShowSetRepModal(true);
                                }}
                                className="flex items-center text-yellow-500 hover:text-yellow-700 text-sm"
                                title="Assign a representative"
                            >
                                <UserPlusIcon className="h-4 w-4 mr-1" />
                                Assign rep
                            </button>
                        );
                    }
                    return <span className="text-gray-500 text-sm">No rep assigned</span>;
                }
                return (
                    <div className="flex items-center">
                        <StarIconSolid className="h-4 w-4 text-yellow-700 mr-1.5 flex-shrink-0" />
                        <div>
                            <div className="text-white text-sm">{rep.firstName} {rep.lastName}</div>
                            <div className="text-xs text-gray-400">{rep.email}</div>
                        </div>
                    </div>
                );
            }
        },
        {
            key: 'team',
            title: 'Team',
            render: (_, company) => {
                const memberCount = company.users?.length || 0;
                const tier = company.subscription?.tier || 'BASIC';
                const maxUsers = company.maxUsers || getTierUserLimit(tier);
                const pendingCount = (company.pendingInvitationsCount || 0) + (company.pendingReferralsCount || 0);
                const atLimit = memberCount >= maxUsers;

                return (
                    <div className="text-center">
                        <div className={`text-sm font-medium ${atLimit ? 'text-yellow-700' : 'text-white'}`}>
                            {memberCount}/{maxUsers}
                        </div>
                        {pendingCount > 0 && (
                            <div className="text-xs text-blue-700">{pendingCount} pending</div>
                        )}
                        {company.maxUsers && company.maxUsers !== getTierUserLimit(tier) && (
                            <div className="text-xs text-purple-700">custom</div>
                        )}
                    </div>
                );
            }
        },
        {
            key: 'subscription',
            title: 'Subscription',
            render: (subscription) => subscription ? (
                <div>
                    <StatusBadge status={subscription.status} />
                    <div className="text-xs text-gray-400 mt-1">{subscription.tier}</div>
                </div>
            ) : (
                <span className="text-gray-400">None</span>
            )
        },
        {
            key: 'active',
            title: 'Status',
            render: (active) => <StatusBadge status={active ? 'active' : 'inactive'} />
        },
        {
            key: 'createdAt',
            title: 'Created',
            sortable: true,
            render: (date) => new Date(date).toLocaleDateString()
        },
        {
            key: 'actions',
            title: 'Actions',
            render: (_, company) => (
                <div className="flex space-x-2">
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            openDetailModal(company);
                        }}
                        className="p-1 text-gray-400 hover:text-primary"
                        title="View details"
                    >
                        <EyeIcon className="h-4 w-4" />
                    </button>
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            openEditModal(company);
                        }}
                        className="p-1 text-gray-400 hover:text-primary"
                        title="Edit company"
                    >
                        <PencilIcon className="h-4 w-4" />
                    </button>
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            openTeamModal(company);
                        }}
                        className="p-1 text-gray-400 hover:text-blue-700"
                        title="Manage team"
                    >
                        <UserGroupIcon className="h-4 w-4" />
                    </button>
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            openFeatureModal(company);
                        }}
                        className="p-1 text-gray-400 hover:text-amber-700"
                        title="Manage features"
                    >
                        <SparklesIcon className="h-4 w-4" />
                    </button>
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            handleCancelPendingJobs(company);
                        }}
                        className="p-1 text-gray-400 hover:text-orange-700"
                        title="Cancel all pending jobs for this company"
                    >
                        <NoSymbolIcon className="h-4 w-4" />
                    </button>
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            openDeleteModal(company);
                        }}
                        className="p-1 text-gray-400 hover:text-red-500"
                        title="Delete company"
                    >
                        <TrashIcon className="h-4 w-4" />
                    </button>
                </div>
            )
        }
    ];

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex justify-between items-center">
                <h1 className="text-2xl font-bold text-white">Companies</h1>
                <button
                    onClick={() => {
                        resetForm();
                        setShowCreateModal(true);
                    }}
                    className="px-4 py-2 bg-primary text-white hover:bg-opacity-90 flex items-center space-x-2"
                >
                    <PlusIcon className="h-4 w-4" />
                    <span>Add Company</span>
                </button>
            </div>

            {/* Filters */}
            <div className="bg-gray-800 p-4">
                <div className="flex items-center space-x-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-300 mb-1">Status</label>
                        <select
                            value={filters.active}
                            onChange={(e) => setFilters(prev => ({ ...prev, active: e.target.value }))}
                            className="bg-gray-700 border border-gray-600 text-white p-2"
                        >
                            <option value="">All</option>
                            <option value="true">Active</option>
                            <option value="false">Inactive</option>
                        </select>
                    </div>
                    <div className="flex items-end">
                        <button
                            onClick={() => setFilters({ active: '' })}
                            className="px-4 py-2 bg-gray-600 text-white hover:bg-gray-500"
                        >
                            Clear Filters
                        </button>
                    </div>
                </div>
            </div>

            {/* Error Display */}
            {error && (
                <div className="bg-red-50 border border-red-200 p-4">
                    <p className="text-red-700">Error: {error}</p>
                </div>
            )}

            {/* Companies Table */}
            <DataTable
                columns={columns}
                data={companies}
                loading={loading}
                onRowClick={(company) => openDetailModal(company)}
            />

            {/* Company Detail Modal */}
            <Modal
                isOpen={showDetailModal}
                onClose={() => {
                    setShowDetailModal(false);
                    setSelectedCompany(null);
                }}
                title="Company Details"
                size="lg"
            >
                {selectedCompany && <CompanyDetail company={selectedCompany} />}
            </Modal>

            {/* Create Company Modal */}
            <ModalForm
                isOpen={showCreateModal}
                onClose={() => {
                    setShowCreateModal(false);
                    resetForm();
                }}
                onSubmit={handleCreateCompany}
                title="Create Company"
                submitLabel="Create Company"
                isLoading={submitLoading}
            >
                <CompanyForm formData={formData} setFormData={setFormData} />
            </ModalForm>

            {/* Edit Company Modal */}
            <ModalForm
                isOpen={showEditModal}
                onClose={() => {
                    setShowEditModal(false);
                    setSelectedCompany(null);
                    resetForm();
                }}
                onSubmit={handleUpdateCompany}
                title="Edit Company"
                submitLabel="Update Company"
                isLoading={submitLoading}
            >
                <CompanyForm formData={formData} setFormData={setFormData} isEdit />
            </ModalForm>

            {/* Feature Management Modal */}
            <FeatureManagementModal
                isOpen={showFeatureModal}
                onClose={() => {
                    setShowFeatureModal(false);
                    setSelectedCompany(null);
                }}
                company={selectedCompany}
            />

            {/* Team Management Modal */}
            <TeamManagementModal
                isOpen={showTeamModal}
                onClose={() => {
                    setShowTeamModal(false);
                    setSelectedCompany(null);
                }}
                company={selectedCompany}
                onUpdate={loadCompanies}
            />

            {/* Delete Company Modal */}
            <Modal
                isOpen={showDeleteModal}
                onClose={closeDeleteModal}
                title="Delete Company"
                size="md"
            >
                {deleteCompanyTarget && (
                    <div className="space-y-4">
                        <div className="border border-red-200 bg-red-50 p-3 text-sm text-red-700 font-mono">
                            <div className="font-bold uppercase mb-1">⚠ Permanent deletion</div>
                            <div>
                                This will permanently delete <b>{deleteCompanyTarget.name}</b>.
                                {' '}It has {deleteCompanyTarget.users?.length || 0} user(s)
                                {deleteCompanyTarget.subscription
                                    ? ` and a subscription (${deleteCompanyTarget.subscription.tier}, ${deleteCompanyTarget.subscription.status})`
                                    : ''}.
                                {' '}Depending on the backend, related users, subscriptions and tenders may be cascade-deleted or orphaned.
                            </div>
                        </div>
                        <div>
                            <label className="block text-sm text-gray-300 mb-1">
                                Type <span className="font-mono text-white">{deleteCompanyTarget.name}</span> to confirm:
                            </label>
                            <input
                                type="text"
                                value={deleteConfirmText}
                                onChange={(e) => setDeleteConfirmText(e.target.value)}
                                className="w-full bg-gray-800 border border-gray-700 text-white p-2 font-mono"
                                autoFocus
                                disabled={deleteLoading}
                            />
                        </div>
                        <div className="flex justify-end gap-2">
                            <button
                                onClick={closeDeleteModal}
                                disabled={deleteLoading}
                                className="px-4 py-2 font-mono text-xs font-bold text-gray-400 bg-gray-800 border border-gray-700 hover:bg-gray-700 hover:text-white uppercase disabled:opacity-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleDeleteCompany}
                                disabled={deleteLoading || deleteConfirmText !== deleteCompanyTarget.name}
                                className="px-4 py-2 font-mono text-xs font-bold uppercase bg-red-600 hover:bg-red-500 text-white disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                                {deleteLoading ? 'Deleting…' : 'Delete Permanently'}
                            </button>
                        </div>
                    </div>
                )}
            </Modal>

            {/* Set Representative Modal */}
            <Modal
                isOpen={showSetRepModal}
                onClose={() => {
                    setShowSetRepModal(false);
                    setSetRepCompany(null);
                }}
                title={`Set Representative for ${setRepCompany?.name || ''}`}
            >
                {setRepCompany && (
                    <div className="space-y-3">
                        <p className="text-sm text-gray-400">Select a user to assign as the company representative:</p>
                        <div className="space-y-2 max-h-64 overflow-y-auto">
                            {setRepCompany.users?.map((user) => (
                                <div key={user.id} className="flex items-center justify-between bg-gray-700 p-3">
                                    <div>
                                        <div className="text-white text-sm">{user.firstName} {user.lastName}</div>
                                        <div className="text-xs text-gray-400">{user.email}</div>
                                    </div>
                                    <button
                                        onClick={() => handleSetRepresentative(user.id, setRepCompany.id)}
                                        disabled={setRepLoading}
                                        className="px-3 py-1 bg-primary text-white text-sm hover:bg-opacity-90 disabled:opacity-50"
                                    >
                                        {setRepLoading ? 'Setting...' : 'Set as Rep'}
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </Modal>
        </div>
    );
};

// Company Detail Component
const CompanyDetail = ({ company }) => {
    const [showFullFingerprint, setShowFullFingerprint] = useState(false);
    const fp = company.fingerprint;

    const formatCurrency = (value) => {
        if (!value) return '-';
        return new Intl.NumberFormat('de-DE', {
            style: 'currency',
            currency: 'EUR',
            maximumFractionDigits: 0
        }).format(value);
    };

    const renderArrayField = (arr, emptyText = '-') => {
        if (!arr || arr.length === 0) return <span className="text-gray-500">{emptyText}</span>;
        return (
            <div className="flex flex-wrap gap-1">
                {arr.map((item, idx) => (
                    <span key={idx} className="px-2 py-0.5 bg-gray-600 text-gray-200 text-xs">
                        {item}
                    </span>
                ))}
            </div>
        );
    };

    return (
        <div className="space-y-6">
            {/* Company ID - Easy Copy */}
            <IdField label="Company ID" value={company.id} />

            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-6">
                <div>
                    <h4 className="text-sm font-medium text-gray-300 mb-2">Basic Information</h4>
                    <div className="space-y-2">
                        <div>
                            <span className="text-gray-400">Name:</span>
                            <span className="text-white ml-2">{company.name}</span>
                        </div>
                        <div>
                            <span className="text-gray-400">Industry:</span>
                            <span className="text-white ml-2">{company.industry || 'Not specified'}</span>
                        </div>
                        <div>
                            <span className="text-gray-400">Size:</span>
                            <span className="text-white ml-2">{company.size?.replace('_', ' ')}</span>
                        </div>
                        <div>
                            <span className="text-gray-400">Location:</span>
                            {company.location ? (
                                <span className="text-white ml-2">
                                    {[company.location.address, company.location.city, company.location.state, company.location.postalCode, company.location.country, company.location.nutsCode, company.location.latitude ? `Lat: ${company.location.latitude}` : null, company.location.longitude ? `Lng: ${company.location.longitude}` : null]
                                        .filter(Boolean)
                                        .join(', ')}
                                </span>
                            ) : (
                                <span className="text-white ml-2">Not specified</span>
                            )}
                        </div>
                        <div>
                            <span className="text-gray-400">Website:</span>
                            <span className="text-white ml-2">
                                {company.website ? (
                                    <a href={company.website} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                                        {company.website}
                                    </a>
                                ) : 'Not specified'}
                            </span>
                        </div>
                        <div>
                            <span className="text-gray-400">Status:</span>
                            <span className="ml-2"><StatusBadge status={company.active ? 'active' : 'inactive'} /></span>
                        </div>
                    </div>
                </div>

                <div>
                    <h4 className="text-sm font-medium text-gray-300 mb-2">Description</h4>
                    <p className="text-white text-sm">
                        {company.description || 'No description provided'}
                    </p>
                </div>
            </div>

            {/* Subscription */}
            {company.subscription && (
                <div>
                    <h4 className="text-sm font-medium text-gray-300 mb-2">Subscription</h4>
                    <div className="bg-gray-700 p-4">
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <span className="text-gray-400">Tier:</span>
                                <span className="text-white ml-2">{company.subscription.tier}</span>
                            </div>
                            <div>
                                <span className="text-gray-400">Status:</span>
                                <span className="ml-2"><StatusBadge status={company.subscription.status} /></span>
                            </div>
                            <div>
                                <span className="text-gray-400">Billing:</span>
                                <span className="text-white ml-2">{company.subscription.billingCycle}</span>
                            </div>
                            <div>
                                <span className="text-gray-400">Price:</span>
                                <span className="text-white ml-2">${company.subscription.price}/month</span>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Company Fingerprint */}
            {fp && (
                <div>
                    <div className="flex justify-between items-center mb-2">
                        <h4 className="text-sm font-medium text-gray-300">
                            Company Fingerprint
                            {fp.profileName && <span className="text-gray-500 ml-2">({fp.profileName})</span>}
                        </h4>
                        <div className="flex items-center space-x-2">
                            <StatusBadge status={fp.isActive ? 'active' : 'inactive'} />
                            <button
                                onClick={() => setShowFullFingerprint(!showFullFingerprint)}
                                className="text-xs text-primary hover:underline"
                            >
                                {showFullFingerprint ? 'Show Less' : 'Show All'}
                            </button>
                        </div>
                    </div>
                    <div className="bg-gray-700 p-4 space-y-4">
                        {/* Core Profile */}
                        <div>
                            <h5 className="text-xs font-medium text-gray-400 uppercase mb-2">Core Profile</h5>
                            <div className="grid grid-cols-2 gap-4 text-sm">
                                <div>
                                    <span className="text-gray-400">Team Size:</span>
                                    <span className="text-white ml-2">{fp.teamSize || '-'}</span>
                                </div>
                                <div>
                                    <span className="text-gray-400">Years in Business:</span>
                                    <span className="text-white ml-2">{fp.yearsInBusiness || '-'}</span>
                                </div>
                            </div>
                            <div className="mt-2">
                                <span className="text-gray-400 text-sm">Primary Services:</span>
                                <div className="mt-1">{renderArrayField(fp.primaryServices)}</div>
                            </div>
                            <div className="mt-2">
                                <span className="text-gray-400 text-sm">Technical Skills:</span>
                                <div className="mt-1">{renderArrayField(fp.technicalSkills)}</div>
                            </div>
                            <div className="mt-2">
                                <span className="text-gray-400 text-sm">Domain Expertise:</span>
                                <div className="mt-1">{renderArrayField(fp.domainExpertise)}</div>
                            </div>
                            {fp.certifications && fp.certifications.length > 0 && (
                                <div className="mt-2">
                                    <span className="text-gray-400 text-sm">Certifications:</span>
                                    <div className="mt-1">{renderArrayField(fp.certifications)}</div>
                                </div>
                            )}
                        </div>

                        {/* Experience */}
                        <div className="border-t border-gray-600 pt-4">
                            <h5 className="text-xs font-medium text-gray-400 uppercase mb-2">Experience</h5>
                            <div className="flex space-x-4 text-sm">
                                {fp.governmentExperience && (
                                    <span className="px-2 py-1 bg-blue-600 text-white text-xs">Government</span>
                                )}
                                {fp.enterpriseExperience && (
                                    <span className="px-2 py-1 bg-purple-600 text-white text-xs">Enterprise</span>
                                )}
                                {fp.startupExperience && (
                                    <span className="px-2 py-1 bg-green-600 text-white text-xs">Startup</span>
                                )}
                                {!fp.governmentExperience && !fp.enterpriseExperience && !fp.startupExperience && (
                                    <span className="text-gray-500">No experience flags set</span>
                                )}
                            </div>
                        </div>

                        {showFullFingerprint && (
                            <>
                                {/* Growth & Strategy */}
                                {(fp.growthAreas?.length > 0 || fp.avoidAreas?.length > 0 || fp.competitiveAdvantages) && (
                                    <div className="border-t border-gray-600 pt-4">
                                        <h5 className="text-xs font-medium text-gray-400 uppercase mb-2">Strategy</h5>
                                        {fp.growthAreas?.length > 0 && (
                                            <div className="mb-2">
                                                <span className="text-gray-400 text-sm">Growth Areas:</span>
                                                <div className="mt-1">{renderArrayField(fp.growthAreas)}</div>
                                            </div>
                                        )}
                                        {fp.avoidAreas?.length > 0 && (
                                            <div className="mb-2">
                                                <span className="text-gray-400 text-sm">Avoid Areas:</span>
                                                <div className="mt-1">{renderArrayField(fp.avoidAreas)}</div>
                                            </div>
                                        )}
                                        {fp.competitiveAdvantages && (
                                            <div>
                                                <span className="text-gray-400 text-sm">Competitive Advantages:</span>
                                                <p className="text-white text-sm mt-1">{fp.competitiveAdvantages}</p>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Hard Filters */}
                                <div className="border-t border-gray-600 pt-4">
                                    <h5 className="text-xs font-medium text-gray-400 uppercase mb-2">Hard Filters</h5>
                                    <div className="grid grid-cols-2 gap-4 text-sm">
                                        {fp.languages?.length > 0 && (
                                            <div>
                                                <span className="text-gray-400">Languages:</span>
                                                <div className="mt-1">{renderArrayField(fp.languages)}</div>
                                            </div>
                                        )}
                                        {fp.cpvCodes?.length > 0 && (
                                            <div>
                                                <span className="text-gray-400">CPV Codes:</span>
                                                <div className="mt-1">{renderArrayField(fp.cpvCodes)}</div>
                                            </div>
                                        )}
                                        {fp.nutsCodes?.length > 0 && (
                                            <div>
                                                <span className="text-gray-400">NUTS Codes:</span>
                                                <div className="mt-1">{renderArrayField(fp.nutsCodes)}</div>
                                            </div>
                                        )}
                                        {fp.contractForms?.length > 0 && (
                                            <div>
                                                <span className="text-gray-400">Contract Forms:</span>
                                                <div className="mt-1">{renderArrayField(fp.contractForms)}</div>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Budget & Timeline */}
                                <div className="border-t border-gray-600 pt-4">
                                    <h5 className="text-xs font-medium text-gray-400 uppercase mb-2">Budget & Timeline</h5>
                                    <div className="grid grid-cols-2 gap-4 text-sm">
                                        <div>
                                            <span className="text-gray-400">Budget Range:</span>
                                            <span className="text-white ml-2">
                                                {fp.budgetMin || fp.budgetMax
                                                    ? `${formatCurrency(fp.budgetMin)} - ${formatCurrency(fp.budgetMax)}`
                                                    : '-'}
                                            </span>
                                        </div>
                                        <div>
                                            <span className="text-gray-400">Max Duration:</span>
                                            <span className="text-white ml-2">
                                                {fp.timelineDurationMonthsMax ? `${fp.timelineDurationMonthsMax} months` : '-'}
                                            </span>
                                        </div>
                                        <div>
                                            <span className="text-gray-400">Min Team Size:</span>
                                            <span className="text-white ml-2">{fp.teamMinSize || '-'}</span>
                                        </div>
                                        <div>
                                            <span className="text-gray-400">Subcontracting:</span>
                                            <span className="text-white ml-2">{fp.subcontractingPolicy || '-'}</span>
                                        </div>
                                    </div>
                                </div>

                                {/* Criteria */}
                                {fp.criteria && fp.criteria.length > 0 && (
                                    <div className="border-t border-gray-600 pt-4">
                                        <h5 className="text-xs font-medium text-gray-400 uppercase mb-2">
                                            Matching Criteria ({fp.criteria.length})
                                        </h5>
                                        <div className="space-y-2 max-h-48 overflow-y-auto">
                                            {fp.criteria.map((criterion) => (
                                                <div key={criterion.id} className="bg-gray-800 p-2 text-sm">
                                                    <div className="flex justify-between items-center">
                                                        <span className="text-white font-medium">{criterion.label}</span>
                                                        <span className="text-gray-400">
                                                            Weight: {(criterion.weight * 100).toFixed(0)}%
                                                        </span>
                                                    </div>
                                                    {criterion.must?.length > 0 && (
                                                        <div className="mt-1">
                                                            <span className="text-green-700 text-xs">Must: </span>
                                                            <span className="text-gray-300 text-xs">{criterion.must.join(', ')}</span>
                                                        </div>
                                                    )}
                                                    {criterion.mustNot?.length > 0 && (
                                                        <div>
                                                            <span className="text-red-700 text-xs">Must Not: </span>
                                                            <span className="text-gray-300 text-xs">{criterion.mustNot.join(', ')}</span>
                                                        </div>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Products */}
                                {(fp.productLines?.length > 0 || fp.productKeywords?.length > 0) && (
                                    <div className="border-t border-gray-600 pt-4">
                                        <h5 className="text-xs font-medium text-gray-400 uppercase mb-2">Products</h5>
                                        {fp.productLines?.length > 0 && (
                                            <div className="mb-2">
                                                <span className="text-gray-400 text-sm">Product Lines:</span>
                                                <div className="mt-1">{renderArrayField(fp.productLines)}</div>
                                            </div>
                                        )}
                                        {fp.productKeywords?.length > 0 && (
                                            <div>
                                                <span className="text-gray-400 text-sm">Keywords:</span>
                                                <div className="mt-1">{renderArrayField(fp.productKeywords)}</div>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>
            )}

            {/* Users */}
            <div>
                <h4 className="text-sm font-medium text-gray-300 mb-2">Users ({company.users?.length || 0})</h4>
                <div className="bg-gray-700 p-4">
                    {company.users && company.users.length > 0 ? (
                        <div className="space-y-2">
                            {company.users.map((user) => (
                                <div key={user.id} className="flex justify-between items-center">
                                    <div>
                                        <span className="text-white">{user.firstName} {user.lastName}</span>
                                        <span className="text-gray-400 text-sm ml-2">({user.email})</span>
                                    </div>
                                    <div className="flex space-x-2">
                                        <span className="px-2 py-1 bg-gray-600 text-gray-300 text-xs">
                                            {user.role}
                                        </span>
                                        <StatusBadge status={user.active ? 'active' : 'inactive'} size="xs" />
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <p className="text-gray-400">No users registered</p>
                    )}
                </div>
            </div>

            {/* Metadata */}
            <div>
                <h4 className="text-sm font-medium text-gray-300 mb-2">Metadata</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                        <span className="text-gray-400">Created:</span>
                        <span className="text-white ml-2">{new Date(company.createdAt).toLocaleString()}</span>
                    </div>
                    <div>
                        <span className="text-gray-400">Updated:</span>
                        <span className="text-white ml-2">{new Date(company.updatedAt).toLocaleString()}</span>
                    </div>
                </div>
            </div>
        </div>
    );
};

// Company Form Component
const CompanyForm = ({ formData, setFormData, isEdit = false }) => {
    return (
        <div className="space-y-4">
            <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Company Name *</label>
                <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    className="w-full bg-gray-700 border border-gray-600 text-white p-2"
                    required
                />
            </div>

            <div className="grid grid-cols-2 gap-4">
                <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">Size *</label>
                    <select
                        value={formData.size}
                        onChange={(e) => setFormData(prev => ({ ...prev, size: e.target.value }))}
                        className="w-full bg-gray-700 border border-gray-600 text-white p-2"
                        required
                    >
                        <option value="FREELANCER">Freelancer</option>
                        <option value="MICRO">Micro (1-9)</option>
                        <option value="SMALL">Small (10-49)</option>
                        <option value="MEDIUM">Medium (50-249)</option>
                        <option value="LARGE">Large (250+)</option>
                    </select>
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">Industry</label>
                    <input
                        type="text"
                        value={formData.industry}
                        onChange={(e) => setFormData(prev => ({ ...prev, industry: e.target.value }))}
                        className="w-full bg-gray-700 border border-gray-600 text-white p-2"
                        placeholder="e.g., Technology, Healthcare"
                    />
                </div>
            </div>

            <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Location</label>
                <div className="space-y-2">
                    <input
                        type="text"
                        value={formData.location.address}
                        onChange={(e) => setFormData(prev => ({
                            ...prev,
                            location: {...prev.location, address: e.target.value}
                        }))}
                        className="w-full bg-gray-700 border border-gray-600 text-white p-2"
                        placeholder="Address"
                    />
                    <input
                        type="text"
                        value={formData.location.city}
                        onChange={(e) => setFormData(prev => ({
                            ...prev,
                            location: {...prev.location, city: e.target.value}
                        }))}
                        className="w-full bg-gray-700 border border-gray-600 text-white p-2"
                        placeholder="City"
                    />
                    <input
                        type="text"
                        value={formData.location.state}
                        onChange={(e) => setFormData(prev => ({
                            ...prev,
                            location: {...prev.location, state: e.target.value}
                        }))}
                        className="w-full bg-gray-700 border border-gray-600 text-white p-2"
                        placeholder="State"
                    />
                    <input
                        type="text"
                        value={formData.location.postalCode}
                        onChange={(e) => setFormData(prev => ({
                            ...prev,
                            location: {...prev.location, postalCode: e.target.value}
                        }))}
                        className="w-full bg-gray-700 border border-gray-600 text-white p-2"
                        placeholder="Postal Code"
                    />
                    <input
                        type="text"
                        value={formData.location.country}
                        onChange={(e) => setFormData(prev => ({
                            ...prev,
                            location: {...prev.location, country: e.target.value}
                        }))}
                        className="w-full bg-gray-700 border border-gray-600 text-white p-2"
                        placeholder="Country"
                    />
                    <input
                        type="text"
                        value={formData.location.nutsCode}
                        onChange={(e) => setFormData(prev => ({
                            ...prev,
                            location: {...prev.location, nutsCode: e.target.value}
                        }))}
                        className="w-full bg-gray-700 border border-gray-600 text-white p-2"
                        placeholder="NUTS Code"
                    />
                    <div className="grid grid-cols-2 gap-2">
                        <input
                            type="number"
                            step="0.000001"
                            value={formData.location.latitude || ''}
                            onChange={(e) => setFormData(prev => ({
                                ...prev,
                                location: {
                                    ...prev.location,
                                    latitude: e.target.value ? parseFloat(e.target.value) : null
                                }
                            }))}
                            className="w-full bg-gray-700 border border-gray-600 text-white p-2"
                            placeholder="Latitude"
                        />
                        <input
                            type="number"
                            step="0.000001"
                            value={formData.location.longitude || ''}
                            onChange={(e) => setFormData(prev => ({
                                ...prev,
                                location: {
                                    ...prev.location,
                                    longitude: e.target.value ? parseFloat(e.target.value) : null
                                }
                            }))}
                            className="w-full bg-gray-700 border border-gray-600 text-white p-2"
                            placeholder="Longitude"
                        />
                    </div>
                </div>
            </div>

            <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Website</label>
                <input
                    type="url"
                    value={formData.website}
                    onChange={(e) => setFormData(prev => ({...prev, website: e.target.value}))}
                    className="w-full bg-gray-700 border border-gray-600 text-white p-2"
                    placeholder="https://example.com"
                />
            </div>

            <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Description</label>
                <textarea
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({...prev, description: e.target.value}))}
                    className="w-full bg-gray-700 border border-gray-600 text-white p-2"
                    rows="3"
                    placeholder="Brief description of the company"
                />
            </div>

            {isEdit && (
                <div className="flex items-center">
                    <input
                        type="checkbox"
                        id="active"
                        checked={formData.active}
                        onChange={(e) => setFormData(prev => ({...prev, active: e.target.checked}))}
                        className="h-4 w-4 text-primary focus:ring-primary border-gray-600"
                    />
                    <label htmlFor="active" className="ml-2 block text-sm text-gray-300">
                        Active
                    </label>
                </div>
            )}
        </div>
    );
};

export default Companies;